import React from "react";
import {
  Typography,
  Button,
  Container,
  Grid,
  Box,
  Avatar,
  LinearProgress,
  Link,
  Divider,
} from "@mui/material";
import {
  Security,
  CheckCircle,
  ArrowForward,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Lock,
  Shield,
  Verified,
  People,
  Block,
  AutoAwesome,
  Timeline,
} from "@mui/icons-material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import { sandooqTheme } from "themes/sandooqTheme";

// ── Hero Card ────────────────────────────────────────────────────────────────
function HeroCircleCard() {
  const members = ["AJ", "BK", "CL", "DM", "You"];
  return (
    <Box
      sx={{
        bgcolor: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 4,
        p: 4,
        backdropFilter: "blur(10px)",
        color: "white",
      }}
    >
      <Typography
        variant="overline"
        sx={{ letterSpacing: "0.15em", opacity: 0.5, fontSize: "0.78rem" }}
      >
        ACTIVE LENDING CIRCLE
      </Typography>

      <Box sx={{ my: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            color: "white",
          }}
        >
          $5,000
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.5, mt: 0.5 }}>
          Example pot · 5 members
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        {members.map((m, i) => (
          <Avatar
            key={m}
            sx={{
              width: 38,
              height: 38,
              fontSize: "0.78rem",
              fontWeight: 700,
              bgcolor:
                m === "You"
                  ? "primary.light"
                  : `rgba(255,255,255,${0.08 + i * 0.03})`,
              border: m === "You" ? "2px solid rgba(255,255,255,0.4)" : "none",
              color: "white",
            }}
          >
            {m}
          </Avatar>
        ))}
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
            opacity: 0.55,
          }}
        >
          <Typography variant="caption">Round 2 of 5</Typography>
          <Typography variant="caption">40%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={40}
          sx={{
            height: 5,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.1)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "primary.light",
              borderRadius: 3,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "rgba(76,175,80,0.15)",
          border: "1px solid rgba(76,175,80,0.3)",
          borderRadius: 2,
          px: 1.5,
          py: 0.5,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "primary.light",
          }}
        />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "primary.light" }}
        >
          Your payout · Round 3
        </Typography>
      </Box>
    </Box>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function SandooqLanding() {
  const navigate = useNavigate();

  const steps = [
    {
      n: "01",
      title: "Join a circle",
      body: "Sign up in minutes and join a circle of people you trust. No ID checks until it's your turn to get paid.",
    },
    {
      n: "02",
      title: "Contribute each round",
      body: "Set it once — contributions happen automatically, no manual transfers.",
    },
    {
      n: "03",
      title: "Receive your payout",
      body: "When it's your turn, the entire pot is yours.",
    },
    {
      n: "04",
      title: "Cycle completes",
      body: "Everyone receives once. Fair, transparent, done.",
    },
  ];

  const features = [
    {
      icon: <Block />,
      title: "Zero interest",
      body: "Keep every dollar. No fees, no interest charges ever.",
    },
    {
      icon: <People />,
      title: "Community driven",
      body: "Built on trust with people you choose to save with.",
    },
    {
      icon: <AutoAwesome />,
      title: "Fully automated",
      body: "Contributions and payouts handled automatically.",
    },
    {
      icon: <Timeline />,
      title: "Full transparency",
      body: "See every transaction and round in real time.",
    },
  ];

  const security = [
    {
      icon: <Lock />,
      title: "End-to-end encryption",
      body: "Bank-level security on all your data and transactions.",
    },
    {
      icon: <Shield />,
      title: "Stripe payments",
      body: "PCI DSS-compliant. Your card never touches our servers.",
    },
    {
      icon: <Verified />,
      title: "Identity verification",
      body: "AWS Cognito authentication keeps bad actors out.",
    },
    {
      icon: <Security />,
      title: "Your data stays yours",
      body: "Never sold, never shared. That's a promise.",
    },
  ];

  return (
    <ThemeProvider theme={sandooqTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #0A2010 0%, #1B5E20 55%, #09223a 100%)",
            pt: { xs: 7, md: 10 },
            pb: { xs: 7, md: 11 },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent, transparent 79px, rgba(255,255,255,0.025) 80px), " +
                "repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(255,255,255,0.025) 80px)",
              pointerEvents: "none",
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative" }}>
            <Grid container spacing={8} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="overline"
                  sx={{
                    color: "primary.light",
                    letterSpacing: "0.18em",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    display: "block",
                    mb: 2.5,
                  }}
                >
                  INTEREST-FREE COMMUNITY LENDING
                </Typography>

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.8rem", md: "4rem" },
                    fontWeight: 700,
                    color: "white",
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                    mb: 3,
                  }}
                >
                  Build wealth,{" "}
                  <Box component="span" sx={{ color: "primary.light" }}>
                    together.
                  </Box>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "1.1rem",
                    lineHeight: 1.75,
                    mb: 5,
                    maxWidth: 480,
                  }}
                >
                  Join a savings circle with people you trust, chip in each
                  round, and receive the whole pot when it's your turn — zero
                  interest, zero fees.
                </Typography>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 5 }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate("/register")}
                    sx={{
                      bgcolor: "primary.light",
                      color: "white",
                      py: 1.5,
                      px: 3.5,
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "primary.main" },
                    }}
                  >
                    Get Started
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate("/how-it-works")}
                    sx={{
                      borderColor: "rgba(255,255,255,0.25)",
                      color: "rgba(255,255,255,0.8)",
                      py: 1.5,
                      px: 3.5,
                      fontSize: "0.95rem",
                      "&:hover": {
                        borderColor: "white",
                        bgcolor: "rgba(255,255,255,0.05)",
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </Box>

                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {[
                    "No interest fees",
                    "Community trusted",
                    "Stripe-secured",
                  ].map((t) => (
                    <Box
                      key={t}
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <CheckCircle
                        sx={{
                          fontSize: 13,
                          color: "primary.light",
                          opacity: 0.7,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 500 }}
                      >
                        {t}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <HeroCircleCard />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* ── HOW IT WORKS ──────────────────────────────────────────────────── */}
        <Box sx={{ bgcolor: "white", py: { xs: 7, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ mb: 6 }}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.main",
                  letterSpacing: "0.18em",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                HOW IT WORKS
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  mt: 1,
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  letterSpacing: "-0.02em",
                }}
              >
                Simple by design.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {steps.map((s) => (
                <Grid item xs={12} sm={6} md={3} key={s.n}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "3.5rem",
                        fontWeight: 800,
                        color: "primary.main",
                        opacity: 0.12,
                        lineHeight: 1,
                        mb: 2,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {s.n}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                    >
                      {s.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.75 }}
                    >
                      {s.body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 8, textAlign: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/register")}
                endIcon={<ArrowForward />}
                sx={{ py: 1.5, px: 4, fontSize: "0.95rem" }}
              >
                Start Your Circle
              </Button>
            </Box>
          </Container>
        </Box>

        {/* ── WHY SANDOOQ ───────────────────────────────────────────────────── */}
        <Box sx={{ bgcolor: "#F5F7F5", py: { xs: 7, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.main",
                  letterSpacing: "0.18em",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                WHY SANDOOQ
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  mt: 1,
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  letterSpacing: "-0.02em",
                }}
              >
                Built different.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {features.map((f) => (
                <Grid item xs={12} sm={6} key={f.title}>
                  <Box
                    sx={{
                      bgcolor: "white",
                      borderRadius: 3,
                      p: 4,
                      height: "100%",
                      borderLeft: "3px solid",
                      borderColor: "primary.main",
                      boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
                      transition: "box-shadow 0.2s",
                      "&:hover": {
                        boxShadow: "0 4px 20px rgba(46,125,50,0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2,
                        bgcolor: "rgba(46,125,50,0.07)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2.5,
                        color: "primary.main",
                      }}
                    >
                      {f.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                    >
                      {f.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.75 }}
                    >
                      {f.body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Partner trust */}
            <Box
              sx={{
                mt: 8,
                pt: 5,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mr: 1 }}
              >
                Trusted infrastructure from
              </Typography>
              {["Stripe", "AWS Cognito"].map((p) => (
                <Box
                  key={p}
                  sx={{
                    px: 2.5,
                    py: 0.75,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "white",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "text.primary" }}
                  >
                    {p}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ── SECURITY ──────────────────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: "#0A1E0B",
            py: { xs: 7, md: 10 },
            color: "white",
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.light",
                  letterSpacing: "0.18em",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                SECURITY
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  mt: 1,
                  fontWeight: 700,
                  color: "white",
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  letterSpacing: "-0.02em",
                }}
              >
                Your safety is non-negotiable.
              </Typography>
            </Box>

            <Grid container spacing={5}>
              {security.map((s) => (
                <Grid item xs={12} sm={6} key={s.title}>
                  <Box sx={{ display: "flex", gap: 2.5 }}>
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "rgba(76,175,80,0.1)",
                        border: "1px solid rgba(76,175,80,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "primary.light",
                        mt: 0.25,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 0.5, color: "white" }}
                      >
                        {s.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.5)",
                          lineHeight: 1.75,
                        }}
                      >
                        {s.body}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ── CTA ───────────────────────────────────────────────────────────── */}
        <Box sx={{ bgcolor: "white", py: { xs: 8, md: 12 } }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: { xs: "2.2rem", md: "3.2rem" },
                  letterSpacing: "-0.02em",
                  mb: 3,
                  lineHeight: 1.15,
                }}
              >
                Start building wealth with your community today.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  mb: 6,
                  maxWidth: 520,
                  mx: "auto",
                  lineHeight: 1.75,
                }}
              >
                Start a circle with your family, friends, or community and reach
                your financial goals — together.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate("/register")}
                  sx={{ py: 1.75, px: 4, fontSize: "0.95rem", fontWeight: 600 }}
                >
                  Create your account
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/how-it-works")}
                  sx={{ py: 1.75, px: 4, fontSize: "0.95rem" }}
                >
                  Learn More
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ── FOOTER ────────────────────────────────────────────────────────── */}
        <Box sx={{ bgcolor: "#0A1E0B", py: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      p: 0.75,
                      bgcolor: "primary.main",
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccountBalanceIcon sx={{ color: "white", fontSize: 20 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      fontWeight: 600,
                      fontSize: "1.25rem",
                      letterSpacing: "0.01em",
                      color: "white",
                    }}
                  >
                    Sanduuq
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.4)",
                    lineHeight: 1.8,
                    maxWidth: 300,
                    mb: 3,
                  }}
                >
                  Build wealth together through interest-free community lending
                  circles.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {[Facebook, Twitter, LinkedIn, Instagram].map((Icon, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        border: "1px solid rgba(255,255,255,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.4)",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.06)",
                          borderColor: "rgba(255,255,255,0.2)",
                          color: "rgba(255,255,255,0.8)",
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 15 }} />
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 2.5,
                    letterSpacing: "0.06em",
                    fontSize: "0.8rem",
                  }}
                >
                  PRODUCT
                </Typography>
                {[
                  { label: "How It Works", href: "/how-it-works" },
                  { label: "FAQ", href: "/how-it-works" },
                ].map((l) => (
                  <Link
                    key={l.label}
                    href={l.href}
                    sx={{
                      display: "block",
                      mb: 1.5,
                      color: "rgba(255,255,255,0.45)",
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      "&:hover": { color: "rgba(255,255,255,0.85)" },
                      transition: "color 0.15s",
                    }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 2.5,
                    letterSpacing: "0.06em",
                    fontSize: "0.8rem",
                  }}
                >
                  LEGAL
                </Typography>
                {[
                  { label: "Privacy Policy", href: "/privacy" },
                  { label: "Terms of Service", href: "/terms" },
                  { label: "Disclaimer", href: "/disclaimer" },
                ].map((l) => (
                  <Link
                    key={l.label}
                    href={l.href}
                    sx={{
                      display: "block",
                      mb: 1.5,
                      color: "rgba(255,255,255,0.45)",
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      "&:hover": { color: "rgba(255,255,255,0.85)" },
                      transition: "color 0.15s",
                    }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Grid>
            </Grid>

            <Divider
              sx={{ borderColor: "rgba(255,255,255,0.07)", mt: 6, mb: 4 }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.25)" }}
              >
                © {new Date().getFullYear()} Sanduuq. All rights reserved.
              </Typography>
              <Box sx={{ display: "flex", gap: 3 }}>
                {[
                  { label: "Privacy", href: "/privacy" },
                  { label: "Terms", href: "/terms" },
                ].map((l) => (
                  <Link
                    key={l.label}
                    href={l.href}
                    sx={{
                      color: "rgba(255,255,255,0.25)",
                      textDecoration: "none",
                      fontSize: "0.8rem",
                      "&:hover": { color: "rgba(255,255,255,0.55)" },
                      transition: "color 0.15s",
                    }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
